import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  Button,
  Divider,
  DrawerBody,
  DrawerFooter,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Spacer,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import { EditorState, RawDraftContentState, convertToRaw } from 'draft-js';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { SearchableTagsSelect } from '@/components/SearchableTagsSelect';
import { GroupSelector } from '@/components/post-form';
import { AnnouncementCheckbox } from '@/components/post-form/AnnouncementCheckbox';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { useDraftAnnouncementOptions } from '@/hooks/useDraftAnnouncementOptions';
import { usePermissions } from '@/hooks/usePermissions';
import { CheckIcon, XIcon } from '@/imports/ui/chakra/feather';
import { AnnouncePostModal } from '@/imports/ui/components/feed-item/AnnouncePostModal';

import { BlogEditModal_PostV2Fragment } from './graphql-types/BlogEditModal_PostV2Fragment';

import { BlogEditor } from './components/BlogEditor';
import { BlogImage } from './components/BlogImage';
import { useSaveBlogAsync } from './hooks/useSaveBlogAsync/useSaveBlogAsync';

export type IBlogForm = {
  title: string;
  content: RawDraftContentState;
  group?: string;
  organization?: string;
  tags?: { _id: string; name: string }[];
  bannerImage?: { imageId?: string } | null;
  isAnnouncement: boolean;
  scheduledPublishDate: Date | null;
};

export enum EEditState {
  TYPING = 'TYPING',
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  FAILED = 'FAILED',
}

export const EEditStateMsg = {
  [EEditState.TYPING]: '',
  [EEditState.SAVING]: 'Saving to drafts...',
  [EEditState.SAVED]: 'Saved',
};

export const BlogEditModal: React.FC<{
  groupId?: string;
  organizationId?: string;
  tags: { _id: string; name: string }[];
  blog: BlogEditModal_PostV2Fragment | undefined;
  postId?: string;
  closeModal: () => void;
  editing?: boolean;
  originalImage?: { imageId: string; imageProxy: string };
  alreadyPublished: boolean;
}> & { blogFragment: DocumentNode } = ({
  groupId,
  organizationId,
  tags,
  postId: initialPostId,
  editing,
  blog,
  alreadyPublished,
  closeModal,
  originalImage,
}) => {
  const [editState, setEditState] = React.useState<{
    state: EEditState | null;
    msg: string;
  }>({
    state: null,
    msg: '',
  });
  const announcementModal = useDisclosure();
  const [isAnnounceLoading, setIsAnnounceLoading] = useBoolean(false);
  const [isPublishLoading, setIsPublishLoading] = useBoolean(false);
  const [postId, setPostId] = React.useState<string | undefined>(initialPostId);
  const { saveBlogAsync } = useSaveBlogAsync({
    setEditState,
  });

  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const methods = useForm<IBlogForm>({
    defaultValues: {
      title: blog?.title ?? '',
      content:
        blog?.content ??
        convertToRaw(EditorState.createEmpty().getCurrentContent()),
      group: blog?.group?._id ?? groupId,
      organization: blog?.organization?._id ?? organizationId,
      tags: blog?.tags ? blog.tags.filter((tag) => tag._id) : tags,
      bannerImage: blog?.bannerImage
        ? { imageId: blog.bannerImage._id }
        : undefined,
      isAnnouncement:
        (!!blog?.announcement?._id || blog?.isAnnouncementScheduled) ?? false,
      scheduledPublishDate: blog?.scheduledPublishDate
        ? new Date(blog.scheduledPublishDate)
        : null,
    },
  });

  const updatedGroup = methods.watch('group');
  const updatedTitle = methods.watch('title');
  const updatedContent = methods.watch('content');
  const updatedOrganization = methods.watch('organization');
  const updatedTags = methods.watch('tags');
  const updatedBannerImage = methods.watch('bannerImage');
  const updatedIsAnnouncement = methods.watch('isAnnouncement');
  const updatedScheduledPublishDate = methods.watch('scheduledPublishDate');

  // id like to include saveBlogAsync in deps but it's broken
  const debouncedSave = React.useMemo(() => {
    return debounce((blog: IBlogForm) => {
      saveBlogAsync({
        postId,
        setPostId,
        alreadyPublished,
        publish: false,
        isAnnouncement: blog.isAnnouncement,
        blog,
      });
    }, 1000);
  }, [postId, alreadyPublished]);

  React.useEffect(() => {
    if (!alreadyPublished) {
      debouncedSave({
        group: updatedGroup,
        title: updatedTitle,
        content: updatedContent,
        organization: updatedOrganization,
        tags: updatedTags,
        bannerImage: updatedBannerImage,
        isAnnouncement: updatedIsAnnouncement,
        scheduledPublishDate: updatedScheduledPublishDate,
      });
    }
  }, [
    alreadyPublished,
    debouncedSave,
    updatedBannerImage,
    updatedContent,
    updatedGroup,
    updatedOrganization,
    updatedTags,
    updatedTitle,
    updatedIsAnnouncement,
    updatedScheduledPublishDate,
  ]);

  React.useEffect(
    () =>
      setEditState({
        state: EEditState.TYPING,
        msg: EEditStateMsg[EEditState.TYPING],
      }),
    [updatedTitle]
  );

  React.useEffect(
    () =>
      setEditState({
        state: EEditState.TYPING,
        msg: EEditStateMsg[EEditState.TYPING],
      }),
    [updatedContent]
  );

  const { showAnnouncementOption } = useDraftAnnouncementOptions({
    isEditing: !!blog,
    isPublished: !!blog?.published,
    groupId: updatedGroup,
  });

  const onInvalid = () => {
    /**
     * If the form is invalid, reset `scheduledPublishDate`
     * User should be able to click `Post` to create the post
     * immediately after the form fields become valid
     */
    methods.resetField('scheduledPublishDate');
  };

  const onPublish = methods.handleSubmit(async (blog) => {
    debouncedSave.cancel();
    const res = await saveBlogAsync({
      postId,
      setPostId,
      alreadyPublished,
      publish: true,
      blog,
      isAnnouncement: false,
    });
    if (res) {
      closeModal();
    }
  }, onInvalid);

  const onAnnounce = methods.handleSubmit(async (blog) => {
    debouncedSave.cancel();
    const res = await saveBlogAsync({
      postId,
      setPostId,
      alreadyPublished,
      publish: true,
      isAnnouncement: true,
      blog,
    });
    if (res) {
      closeModal();
    }
  }, onInvalid);

  return (
    <>
      <DrawerBody p="0">
        <FormProvider {...methods}>
          <Stack spacing="4" h="full" w="full">
            <BlogImage originalImage={originalImage} />
            <Controller
              name="title"
              render={({ field: { onChange, value, ref } }) => (
                <FormControl
                  px="6"
                  isInvalid={!!methods.formState.errors.title}
                >
                  <FormLabel>Article Title</FormLabel>
                  <Input
                    placeholder="What is your article about?"
                    h={14}
                    fontSize="3xl"
                    fontWeight="semibold"
                    value={value}
                    onChange={onChange}
                    ref={ref}
                  />
                  <FormErrorMessage>
                    {methods.formState.errors.title?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
              rules={{
                required: 'Title is required',
              }}
            />
            <Controller
              // TODO: Not a regression, but feels like content should be required for an article
              name="content"
              render={({ field: { onChange, value, ref, onBlur } }) => (
                <Box px="6" flexGrow="1" overflowY="hidden" minH={60}>
                  <FormControl
                    isInvalid={!!methods.formState.errors.content}
                    h="full"
                    display="flex"
                    flexDirection="column"
                  >
                    <FormLabel>Content</FormLabel>
                    <BlogEditor
                      placeholder="Write the content for your article"
                      ariaLabel="blog content"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                    />
                  </FormControl>
                </Box>
              )}
            />
            <Divider />
            <Stack w="full" px="6">
              <Heading as="h6" size="xs">
                Settings
              </Heading>

              {!organizationId && (
                <GroupSelector isDisabled={!editing && !!groupId} />
              )}

              <FormControl>
                <Controller
                  name="tags"
                  render={({ field: { onChange, value, ref, onBlur } }) => (
                    <SearchableTagsSelect
                      placement="top"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                    />
                  )}
                />
              </FormControl>

              {showAnnouncementOption && (
                <Box>
                  <AnnouncementCheckbox />
                  <AnnouncePostModal
                    open={announcementModal.isOpen}
                    loading={isPublishLoading}
                    announcementLoading={isAnnounceLoading}
                    onClose={announcementModal.onClose}
                    onClickAnnounce={async () => {
                      setIsAnnounceLoading.on();
                      if (!!onAnnounce) {
                        await onAnnounce();
                      } else {
                        await onPublish();
                      }
                      setIsAnnounceLoading.off();
                    }}
                    onClickPost={async () => {
                      setIsPublishLoading.on();
                      await onPublish();
                      setIsPublishLoading.off();
                    }}
                    postType={'Article'}
                    postVerb={'Publish'}
                    postVerbPresentTense={'Publishing'}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter>
        <HStack w="full">
          <HStack>
            <Text
              textColor={
                editState.state === EEditState.SAVING
                  ? 'gray'
                  : editState.state === EEditState.SAVED
                  ? 'green'
                  : editState.state === EEditState.FAILED
                  ? 'red'
                  : undefined
              }
            >
              {editState.msg}
            </Text>
            {editState.state === EEditState.SAVED && (
              <CheckIcon color="green" />
            )}
            {editState.state === EEditState.FAILED && (
              <XIcon name="x" color="red" />
            )}
          </HStack>
          <Spacer />
          {!alreadyPublished && (
            <>
              {!updatedScheduledPublishDate && (
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => {
                    debouncedSave.flush();
                    closeModal();
                  }}
                >
                  Save Draft
                </Button>
              )}
              {canSchedulePosts && (
                <Controller
                  name="scheduledPublishDate"
                  control={methods.control}
                  render={({
                    field: { onBlur, onChange, ref, value, name },
                  }) => (
                    <SchedulePostMenu
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      isDisabled={
                        methods.formState.isSubmitted &&
                        !methods.formState.isValid
                      }
                      validate={() => {
                        if (!value) {
                          methods.setError(name, {
                            message: 'A date and time must be specified',
                          });
                          return false;
                        } else {
                          methods.clearErrors(name);
                          return true;
                        }
                      }}
                      validationMessage={
                        methods.formState.errors.scheduledPublishDate?.message
                      }
                    />
                  )}
                />
              )}
            </>
          )}
          {!alreadyPublished ? (
            <>
              {canSchedulePosts && updatedScheduledPublishDate ? (
                <Button
                  isDisabled={!methods.formState.isValid}
                  onClick={() => {
                    debouncedSave.flush();
                    closeModal();
                  }}
                >
                  Schedule Post
                </Button>
              ) : (
                <Button
                  isDisabled={!methods.formState.isValid}
                  isLoading={isPublishLoading}
                  onClick={async () => {
                    setIsPublishLoading.on();
                    updatedIsAnnouncement
                      ? await onAnnounce()
                      : await onPublish();
                    setIsPublishLoading.off();
                  }}
                >
                  Post Now
                </Button>
              )}
            </>
          ) : (
            <Button
              isDisabled={!methods.formState.isValid}
              isLoading={isPublishLoading}
              onClick={async () => {
                setIsPublishLoading.on();
                await onPublish();
                setIsPublishLoading.off();
              }}
            >
              Update
            </Button>
          )}
        </HStack>
      </DrawerFooter>
    </>
  );
};

BlogEditModal.blogFragment = gql`
  fragment BlogEditModal_PostV2Fragment on BlogV2 {
    _id
    scheduledPublishDate
    isAnnouncementScheduled
    organization {
      _id
      name
    }
    group {
      _id
      isGlobalSingleton
    }
    announcement {
      _id
    }
    published
    title
    content
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    tagIds
    tags {
      _id
      name
    }
  }
`;
